<template>
  <div class="frpc">
    <div class="frpc-header">
      <div class="frpc-avatar" :style="avatarStyle">{{ initials }}</div>
      <div class="frpc-meta">
        <div class="frpc-author">{{ item.authorName }}</div>
        <div class="frpc-sub">
          <span v-if="item.groupName" class="frpc-group">{{ item.groupName }} · </span>
          {{ item.timeAgo || '' }}
        </div>
      </div>
      <span class="frpc-badge"><f7-icon f7="repeat" size="11" />{{$t('post.reposted')}} </span>
    </div>

    <div v-if="item.text" class="frpc-text">{{ item.text }}</div>

    <div class="frpc-quote">
      <FeedRepostQuote :item="originalPost" />
    </div>

    <FeedReactionBar
      :reacted="userReacted"
      :reactionType="userReactionType"
      :reactionCount="totalReactions"
      :reactions="item.reactions || []"
      :commentsCount="item.commentsCount || 0"
      :repostsCount="repostsCount"
      :share-content="shareContent"
      @react="onReact"
      @comment="onCommentToggle"
      @repost="showRepostComposer = true"
      @shareToChat="showShareToChat = true"
    />

    <div v-if="showComments" class="frpc-comments">
      <FeedCommentThread
        :comments="item.comments || []"
        :current-user-id="currentUserId"
        @react-comment="onReactComment"
        @reply="onReply"
        @load-more-replies="$emit('load-more-replies', $event)"
      />
      <FeedCommentComposer
        :placeholder="$t('post.addComment')"
        class="frpc-comment-composer"
        @submit="onCommentSubmit"
      />
    </div>

    <ShareToChatSheet
      v-model:opened="showShareToChat"
      :groups="chatGroups"
      @selectGroup="onShareToChat"
    />

    <FeedRepostComposer
      v-model:opened="showRepostComposer"
      :item="originalPost"
      :user-name="userName"
      :user-avatar="userAvatar"
      @submit="onRepostSubmit"
    />

    <FeedCommentsSheet
      v-model:opened="showCommentsSheet"
      :comments="item.comments || []"
      :comments-count="item.commentsCount || 0"
      :reactions="item.reactions || []"
      :reposts-count="repostsCount"
      :current-user-id="currentUserId"
      @comment="onCommentSubmit"
      @react-comment="onReactComment"
      @reply="onReply"
      @load-more-replies="$emit('load-more-replies', $event)"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import FeedReactionBar from './FeedReactionBar.vue'
import FeedCommentThread from './FeedCommentThread.vue'
import FeedCommentComposer from './FeedCommentComposer.vue'
import FeedRepostQuote from './FeedRepostQuote.vue'
import FeedRepostComposer from './FeedRepostComposer.vue'
import FeedCommentsSheet from './FeedCommentsSheet.vue'
import ShareToChatSheet from '../tamtam/ShareToChatSheet.vue'
import { useLargeScreen } from '../../js/composables/useMediaQuery.js'

const props = defineProps({
  item:         { type: Object, required: true },
  currentUserId: { type: String, default: '' },
  chatGroups:   { type: Array,  default: () => [] },
  userName:     { type: String, default: '' },
  userAvatar:   { type: String, default: '' },
})

const emit = defineEmits(['react', 'comment', 'repost', 'shareToChat', 'react-comment', 'reply', 'load-more-replies'])

const showComments = ref(false)
const showCommentsSheet = ref(false)
const showShareToChat = ref(false)
const showRepostComposer = ref(false)

const isLargeScreen = useLargeScreen().matches

const originalPost = computed(() => {
  let current = props.item?.originalPost || props.item
  let guard = 0
  while (current?.originalPost && guard < 5) {
    current = current.originalPost
    guard++
  }
  return current
})

const initials = computed(() => {
  const name = props.item.authorName || ''
  return name.split(' ').map((w) => w[0]).join('').substring(0, 2).toUpperCase()
})

const avatarStyle = computed(() => {
  const colors = ['#1A6B42', '#3B4FCA', '#C9A84C', '#C0440A', '#6B46C1']
  const hash = (props.item.authorName || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  const bg = colors[hash % colors.length]
  return { background: bg, color: '#fff' }
})

const userReacted = computed(() => {
  if (!props.item.reactions || !props.currentUserId) return false
  return props.item.reactions.some((r) => r.users?.includes(props.currentUserId))
})

const userReactionType = computed(() => {
  if (!props.item.reactions || !props.currentUserId) return 'LIKE'
  const r = props.item.reactions.find((r) => r.users?.includes(props.currentUserId))
  return r?.reactionType || 'LIKE'
})

const totalReactions = computed(() => {
  if (!props.item.reactions) return 0
  return props.item.reactions.reduce((sum, r) => sum + (r.count || 0), 0)
})

const repostsCount = computed(() => {
  if (!props.item.reactions) return 0
  const r = props.item.reactions.find((r) => r.reactionType === 'Repost')
  return r?.count || 0
})

const shareContent = computed(() => ({
  subject: props.item.text || originalPost.value.text || originalPost.value.headline || '',
  message: props.item.text || originalPost.value.text || originalPost.value.headline || '',
  url: `${window.location.origin}/post/${props.item.id}`,
  files: [],
}))

function onReact(reactionType) {
  emit('react', { itemId: props.item.id, reactionType })
}

function onCommentToggle() {
  if (isLargeScreen.value) {
    showComments.value = !showComments.value
  } else {
    showComments.value = false
    showCommentsSheet.value = true
  }
}

function onRepostSubmit({ postId, text }) {
  emit('repost', { itemId: postId, text })
}

function onReactComment({ commentId, replyId, reactionType }) {
  emit('react-comment', { postId: props.item.id, commentId, replyId, reactionType })
}

function onReply({ commentId, replyId, text, attachments, replyTo }) {
  emit('reply', { postId: props.item.id, commentId, replyId, text, attachments, replyTo })
}

function onCommentSubmit({ text, attachments }) {
  emit('comment', { postId: props.item.id, text, attachments })
}

function onShareToChat(group) {
  emit('shareToChat', { group, feedItem: props.item })
}
</script>

<style scoped>
.frpc {
  background: var(--f7-page-bg-color, #fff);
  border-radius: 10px;
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  padding: 10px 12px;
  margin-bottom: 8px;
  content-visibility: auto;
  contain-intrinsic-size: 260px;
}
.frpc-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;
}
.frpc-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}
.frpc-meta { flex: 1; min-width: 0; }
.frpc-author { font-size: 12px; font-weight: 500; }
.frpc-sub { font-size: 10px; color: var(--f7-on-surface-variant, #8e8e93); }
.frpc-group { font-weight: 500; }
.frpc-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 500;
  color: var(--avocash-green, #1A6B42);
  background: #EAF2EB;
  padding: 2px 8px;
  border-radius: 8px;
  flex-shrink: 0;
}
.frpc-text {
  font-size: 12px;
  line-height: 1.55;
  color: var(--f7-text-color, #000);
  margin-bottom: 6px;
  white-space: pre-wrap;
  word-break: break-word;
}
.frpc-quote { margin-bottom: 6px; }
.frpc-comments {
  margin-top: 6px;
  border-top: 0.5px solid var(--f7-list-item-border-color, #e0e0e0);
  padding-top: 6px;
}
.frpc-comment-composer {
  margin-top: 8px;
}
</style>
